// VERIFY: new file — auth API + axios interceptor + fetch auth header helper
import axios from 'axios';
import { BASE_URL } from './helpers';
import { pauseForRateLimit } from './rateLimitPause';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

export interface AuthUser {
  user_id: string;
  username: string;
  display_name: string;
}

export interface LoginResponse {
  token: string;
  user_id: string;
  username: string;
  display_name: string;
}

// ── Token storage ──

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setAuth(token: string, user: AuthUser): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

// Logout and a 401 both end the session through clearAuth, and the page is not
// reloaded after either. Anything that caches per-user data in module state
// subscribes here to drop it with the session.
const authClearedListeners = new Set<() => void>();

export function onAuthCleared(listener: () => void): () => void {
  authClearedListeners.add(listener);
  return () => {
    authClearedListeners.delete(listener);
  };
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  authClearedListeners.forEach((listener) => listener());
}

// ── API call ──

export async function login(username: string, password: string): Promise<LoginResponse> {
  const res = await axios.post<LoginResponse>(`${BASE_URL}/auth/login`, { username, password });
  return res.data;
}

// ── Axios interceptor ──

let interceptorInstalled = false;
let onUnauthorized: (() => void) | null = null;

export interface CreditsErrorPayload {
  code: string;
  message?: string;
  have?: number;
  need?: number;
  status: number;
}

let onCreditsError: ((e: CreditsErrorPayload) => void) | null = null;

export function setOnUnauthorized(cb: () => void): void {
  onUnauthorized = cb;
}

export function setOnCreditsError(cb: (e: CreditsErrorPayload) => void): void {
  onCreditsError = cb;
}

export function installAxiosInterceptor(): void {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  // Request: attach Bearer token
  axios.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response: handle 401 + 402 + 429
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      if (status === 401 && onUnauthorized) {
        onUnauthorized();
      } else if (status === 402 && onCreditsError) {
        const body = error.response?.data ?? {};
        onCreditsError({
          code: typeof body.error === 'string' ? body.error : 'credits_error',
          message: typeof body.message === 'string' ? body.message : undefined,
          have: typeof body.have === 'number' ? body.have : undefined,
          need: typeof body.need === 'number' ? body.need : undefined,
          status: 402,
        });
      } else if (status === 429) {
        pauseForRateLimit(error.response);
      }
      return Promise.reject(error);
    }
  );
}

// Install immediately on module load — must be before first render
installAxiosInterceptor();

// ── Helper for raw fetch calls (e.g. builder SSE) ──

export function getAuthHeaders(): Record<string, string> {
  const token = getToken();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}
